import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { AuthSessionService } from '@core/auth/auth-session.service';
import { SessionCompany } from '@core/auth/auth-session.model';
import { setCreateBusinessIntent, clearCreateBusinessIntent } from '@core/auth/create-business-intent.storage';
import { PermissionContextService } from '@core/auth/permission-context.service';
import { LocationContextService } from '@core/tenant/location-context.service';
import { TenantContextService } from '@core/tenant/tenant-context.service';
import { AdminShellModeService } from '@core/shell/admin-shell-mode.service';
import { WorkspaceCompanyCardComponent } from '@core/shell/workspace-company-card/workspace-company-card.component';
import { userOwnsAnyCompany } from '@core/auth/company-membership-label.util';
import {
  defaultEntryUrlForCompany,
  isStoredWorkspaceValid,
  resolvePostLoginDestination,
} from '@features/auth/utils/resolve-post-login-destination.util';
import { applyShellModeForEntryUrl } from '@features/auth/utils/navigate-after-staff-auth.util';
import { resolveTenantEntryFromPermissions } from '@features/auth/utils/resolve-tenant-entry-url.util';
import { markWelcomeBeatPending } from '@features/auth/utils/welcome-beat.storage';
import { AuthShowcaseAsideComponent } from '@features/auth/presentation/components/auth-showcase-aside/auth-showcase-aside.component';
import { MaterialModule } from 'src/app/material.module';

@Component({
  selector: 'app-choose-workspace',
  standalone: true,
  imports: [
    RouterModule,
    MaterialModule,
    TranslateModule,
    TablerIconsModule,
    WorkspaceCompanyCardComponent,
    AuthShowcaseAsideComponent,
  ],
  templateUrl: './choose-workspace.component.html',
  styleUrl: './choose-workspace.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChooseWorkspaceComponent {
  private readonly authSession = inject(AuthSessionService);
  private readonly tenantContext = inject(TenantContextService);
  private readonly locationContext = inject(LocationContextService);
  private readonly adminShellMode = inject(AdminShellModeService);
  private readonly permissionContext = inject(PermissionContextService);
  private readonly router = inject(Router);

  readonly user = computed(() => this.authSession.user());
  readonly companies = computed(() => this.user()?.companies ?? []);
  readonly alreadyOwnsBusiness = computed(() => userOwnsAnyCompany(this.companies()));
  readonly createTitleKey = computed(() =>
    this.alreadyOwnsBusiness()
      ? 'auth.chooseWorkspace.createAnotherBusiness'
      : 'auth.chooseWorkspace.openOwnBusiness'
  );
  readonly createHintKey = computed(() =>
    this.alreadyOwnsBusiness()
      ? 'auth.chooseWorkspace.createAnotherBusinessHint'
      : 'auth.chooseWorkspace.openOwnBusinessHint'
  );

  readonly resume = computed(() => {
    const session = this.user();
    if (!session) {
      return null;
    }
    const companyId = this.tenantContext.activeCompanyId();
    const locationId = this.locationContext.activeLocationId();
    if (!isStoredWorkspaceValid(session, companyId, locationId)) {
      return null;
    }
    const company = session.companies.find((row) => row.id === companyId);
    if (!company) {
      return null;
    }
    return { company };
  });

  constructor() {
    const session = this.authSession.user();
    if (!session) {
      void this.router.navigate(['/authentication/login'], { replaceUrl: true });
      return;
    }
    const destination = resolvePostLoginDestination(session);
    if (destination.kind !== 'choose-workspace') {
      void this.leaveToDestination(destination);
    }
  }

  continueWhereLeft(): void {
    const resume = this.resume();
    if (!resume) {
      return;
    }
    const locs = resume.company.locations ?? [];
    this.enter(resume.company.id, locs[0]?.id ?? null, defaultEntryUrlForCompany(resume.company));
  }

  pickCompany(company: SessionCompany): void {
    const locs = company.locations ?? [];
    this.enter(company.id, locs[0]?.id ?? null, defaultEntryUrlForCompany(company));
  }

  private enter(companyId: number, locationId: number | null, entryUrl = '/dashboard'): void {
    clearCreateBusinessIntent();
    this.tenantContext.setActiveCompany(companyId);
    if (locationId != null) {
      this.locationContext.setActiveLocation(locationId);
    } else {
      this.locationContext.clear();
    }
    this.adminShellMode.setMode('tenant');
    const session = this.authSession.user();
    if (!session) {
      void this.router.navigateByUrl(entryUrl, { replaceUrl: true });
      return;
    }
    this.permissionContext.load().subscribe({
      next: () => {
        const refined = resolveTenantEntryFromPermissions(session.roles, (resource, action) =>
          this.permissionContext.can(resource, action)
        );
        applyShellModeForEntryUrl(this.adminShellMode, refined.url);
        void this.router.navigateByUrl(refined.url, { replaceUrl: true });
      },
      error: () => {
        applyShellModeForEntryUrl(this.adminShellMode, entryUrl);
        void this.router.navigateByUrl(entryUrl, { replaceUrl: true });
      },
    });
  }

  private leaveToDestination(
    destination: ReturnType<typeof resolvePostLoginDestination>
  ): void {
    if (destination.kind === 'onboard') {
      markWelcomeBeatPending();
      void this.router.navigate(['/onboard'], { replaceUrl: true });
      return;
    }
    if (destination.kind === 'platform') {
      this.adminShellMode.applyPostLoginDefaultForSuperadmin();
      void this.router.navigate(['/platform/tenants'], { replaceUrl: true });
      return;
    }
    if (destination.kind === 'enter') {
      this.enter(destination.companyId, destination.locationId, destination.entryUrl);
    }
  }

  openOwnBusiness(): void {
    setCreateBusinessIntent();
    this.tenantContext.clear();
    this.locationContext.clear();
    void this.router.navigate(['/onboard'], { replaceUrl: true });
  }
}
